import { Button, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Input } from "@majitap/ui/arc/input/input";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { messageFor } from "../../error-copy";
import { type InvoiceSettings, invoiceSettingsQuery } from "../../invoices";
import { SETTINGS_MENU } from "../../nav";
import styles from "./invoice-details.module.css";

const item = SETTINGS_MENU.flatMap((s) => s.items).find((i) => i.id === "invoice-details")!;

type MpesaKind = InvoiceSettings["mpesa_kind"];
type AccountSource = InvoiceSettings["mpesa_account_source"];
/** The free-text details: every field but how M-Pesa is paid. */
type Key = Exclude<keyof InvoiceSettings, "mpesa_kind" | "mpesa_account_source">;
type Field = {
  key: Key;
  label: string;
  help?: string;
  type?: string;
  inputMode?: "numeric" | "tel" | "email";
  narrow?: boolean;
};
export type Values = Record<Key, string> & { mpesa_kind: MpesaKind; mpesa_account_source: AccountSource };

/** The sections of plain fields, in the order the invoice shows them; a row's fields sit side by side. */
export const SECTIONS: { title: string; note: string; rows: Field[][] }[] = [
  {
    title: "Company",
    note: "At the top of every invoice. Use the name and PIN registered with KRA, the one the bank account is in.",
    rows: [
      [{ key: "legal_name", label: "Legal name" }],
      [{ key: "tax_pin", label: "KRA PIN", narrow: true }],
      [{ key: "address", label: "Address" }],
    ],
  },
  {
    title: "Contact",
    note: "At the foot of every invoice, for questions about it.",
    rows: [
      [
        { key: "support_email", label: "Email", type: "email", inputMode: "email" },
        { key: "support_phone", label: "Phone", type: "tel", inputMode: "tel" },
      ],
    ],
  },
  {
    title: "Bank transfer",
    note: "Shown under Payment details while an invoice is owed. Leave it blank to show no bank account.",
    rows: [
      [{ key: "bank_account_name", label: "Account name" }],
      [
        { key: "bank_account_number", label: "Account number" },
        { key: "bank_account_currency", label: "Currency", help: "3 letters, like KES." },
      ],
      [
        { key: "bank_name", label: "Bank" },
        { key: "bank_branch", label: "Branch" },
      ],
      [
        { key: "bank_code", label: "Bank code", inputMode: "numeric" },
        { key: "bank_branch_code", label: "Branch code", inputMode: "numeric" },
        { key: "bank_swift_code", label: "SWIFT code", help: "8 or 11 characters." },
      ],
    ],
  },
];
const KEYS: Key[] = [...SECTIONS.flatMap((s) => s.rows.flat().map((f) => f.key)), "mpesa_number", "mpesa_account"];

const valuesOf = (s: InvoiceSettings): Values =>
  ({
    ...Object.fromEntries(KEYS.map((k) => [k, s[k] ?? ""])),
    mpesa_kind: s.mpesa_kind,
    mpesa_account_source: s.mpesa_account_source,
  }) as Values;

/** Payers enter a fixed account number only under a Pay Bill that uses one. */
const takesFixedAccount = (v: Pick<Values, "mpesa_kind" | "mpesa_account_source">) =>
  v.mpesa_kind === "paybill" && v.mpesa_account_source === "fixed";

/** What changed, trimmed: a cleared field is sent as null, and so is an account number no longer used. */
export function changesOf(current: InvoiceSettings, values: Values) {
  const out: Partial<InvoiceSettings> = {};
  for (const key of KEYS) {
    const next = key === "mpesa_account" && !takesFixedAccount(values) ? null : values[key].trim() || null;
    if (next !== current[key]) out[key] = next;
  }
  if (values.mpesa_kind !== current.mpesa_kind) out.mpesa_kind = values.mpesa_kind;
  if (values.mpesa_account_source !== current.mpesa_account_source)
    out.mpesa_account_source = values.mpesa_account_source;
  return out;
}

/**
 * Settings › Company › Invoice details (P8-70): what invoices say about the company, its contact,
 * and how customers pay it (bank transfer; M-Pesa by Pay Bill or Till), beside a live preview of
 * the invoice's payment details. Needs `company.manage`; every change is in the audit log.
 */
export function InvoiceDetailsPage(): ReactNode {
  const settings = useQuery(invoiceSettingsQuery);
  if (settings.status === "pending")
    return (
      <>
        <PageHeader title={item.label} description={item.description} />
        <PageLoading label="Loading the invoice details" />
      </>
    );
  if (settings.status === "error")
    return (
      <>
        <PageHeader title={item.label} description={item.description} />
        <PageError message={messageFor(errorOf(settings.error))} onRetry={() => settings.refetch()} />
      </>
    );
  return <DetailsForm current={settings.data} />;
}

const FORM_ID = "invoice-details";

function DetailsForm({ current }: { current: InvoiceSettings }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(() => valuesOf(current));
  const changes = changesOf(current, values);
  const changed = Object.keys(changes).length > 0;
  const save = useMutation({
    mutationFn: () => ok(api.PUT("/v1/admin/billing/invoice-settings", { body: changes })),
    onSuccess: (saved) => queryClient.setQueryData(invoiceSettingsQuery.queryKey, saved),
  });
  const set = (patch: Partial<Values>) => {
    save.reset();
    setValues((v) => ({ ...v, ...patch }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (changed) save.mutate();
  };
  const input = (f: Field) => (
    <Input
      key={f.key}
      className={f.narrow ? styles.narrowField : undefined}
      label={f.label}
      description={f.help}
      type={f.type}
      inputMode={f.inputMode}
      value={values[f.key]}
      onChange={(e) => set({ [f.key]: e.target.value })}
    />
  );
  const till = values.mpesa_kind === "till";
  return (
    <>
      <PageHeader
        title={item.label}
        description={item.description}
        actions={
          <Button type="submit" form={FORM_ID} disabled={!changed} loading={save.isPending}>
            Save
          </Button>
        }
      />
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {messageFor(errorOf(save.error))}
        </Alert>
      ) : null}
      {save.isSuccess && !changed ? (
        <Alert tone="success" title="Saved">
          Invoices show these details from now on, including ones already issued when they are downloaded or sent again.
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <form id={FORM_ID} onSubmit={submit} aria-label="Invoice details">
          {SECTIONS.map((section) => (
            <section key={section.title} className={styles.section}>
              <div className={styles.head}>
                <h2 className={styles.title}>{section.title}</h2>
                <p className={styles.note}>{section.note}</p>
              </div>
              <div className={styles.fields}>
                {section.rows.map((row) =>
                  row.length === 1 ? (
                    input(row[0] as Field)
                  ) : (
                    <div key={row[0]?.key} className={styles.row}>
                      {row.map(input)}
                    </div>
                  ),
                )}
              </div>
            </section>
          ))}
          <section className={styles.section}>
            <div className={styles.head}>
              <h2 className={styles.title}>M-Pesa</h2>
              <p className={styles.note}>
                A Pay Bill, with its account number, or a Till number for Buy Goods. Shown with the bank account; the QR
                code still opens each invoice's own pay link.
              </p>
            </div>
            <div className={styles.fields}>
              <div>
                <p className={styles.legend}>Pay with</p>
                <SegmentedControl
                  label="Pay with"
                  value={values.mpesa_kind}
                  onValueChange={(v) => set({ mpesa_kind: v as MpesaKind })}
                  options={[
                    { value: "paybill", label: "Pay Bill" },
                    { value: "till", label: "Till number" },
                  ]}
                />
              </div>
              <Input
                className={styles.narrowField}
                label={till ? "Till number" : "Pay Bill number"}
                description={till ? "5 to 7 digits. Payers use Lipa na M-Pesa › Buy Goods." : "5 to 7 digits."}
                inputMode="numeric"
                value={values.mpesa_number}
                onChange={(e) => set({ mpesa_number: e.target.value })}
              />
              {till ? null : (
                <div>
                  <p className={styles.legend}>Account number</p>
                  <RadioCards
                    aria-label="Account number"
                    layout="list"
                    value={values.mpesa_account_source}
                    onValueChange={(v) => set({ mpesa_account_source: v as AccountSource })}
                    options={[
                      {
                        value: "invoice_number",
                        label: "The invoice number",
                        description: "Payers enter the invoice's own number, so each payment finds its invoice.",
                      },
                      {
                        value: "fixed",
                        label: "A fixed account number",
                        description: "The same on every invoice.",
                      },
                    ]}
                  />
                </div>
              )}
              {takesFixedAccount(values) ? (
                <Input
                  className={styles.narrowField}
                  label="Fixed account number"
                  description="What payers enter as the account under the Pay Bill."
                  value={values.mpesa_account}
                  onChange={(e) => set({ mpesa_account: e.target.value })}
                />
              ) : null}
            </div>
          </section>
          <p className={styles.audit}>
            <Lock size={16} aria-hidden="true" />
            Only company administrators can change these. Every change is in the Audit log, with what it was before.
          </p>
          <div className={styles.footer}>
            <Button type="submit" disabled={!changed} loading={save.isPending}>
              Save
            </Button>
          </div>
        </form>
        <Preview values={values} />
      </div>
    </>
  );
}

/** A row of the preview: nothing when blank, as on the invoice. */
function Kv({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className={styles.kv}>
      <span>{label}</span>
      <span className={mono ? styles.mono : undefined}>{value}</span>
    </div>
  );
}

const joined = (sep: string, ...parts: string[]) =>
  parts
    .map((p) => p.trim())
    .filter(Boolean)
    .join(sep);

/** The invoice's company block, payment details and contact line, as the PDF draws them. */
export function Preview({ values: v }: { values: Values }) {
  const bank = {
    name: v.bank_account_name.trim(),
    number: joined(" · ", v.bank_account_number, v.bank_account_number.trim() ? v.bank_account_currency : ""),
    branch: joined(", ", v.bank_name, v.bank_branch),
    codes: joined(
      ", ",
      v.bank_code.trim() ? `Bank ${v.bank_code.trim()}` : "",
      v.bank_branch_code.trim() ? `branch ${v.bank_branch_code.trim()}` : "",
    ),
    swift: v.bank_swift_code.trim().toUpperCase(),
  };
  const hasBank = Object.values(bank).some(Boolean);
  const number = v.mpesa_number.trim();
  const till = v.mpesa_kind === "till";
  const account = till ? null : v.mpesa_account_source === "invoice_number" ? "INV-2026-0042" : v.mpesa_account.trim();
  const contact = joined(" · ", v.support_email, v.support_phone);
  return (
    <aside className={styles.preview} aria-label="On the invoice">
      <div className={styles.previewHead}>
        <h2 className={styles.title}>On the invoice</h2>
        <span className={styles.note}>Live preview</span>
      </div>
      <div className={styles.paper}>
        <div className={styles.paperBody}>
          <div className={styles.paperCompany}>
            <strong>{v.legal_name.trim() || "Your company's legal name"}</strong>
            {v.address.trim() ? <span className={styles.paperMuted}>{v.address.trim()}</span> : null}
            {v.tax_pin.trim() ? (
              <span className={styles.paperMuted}>KRA PIN {v.tax_pin.trim().toUpperCase()}</span>
            ) : null}
          </div>
          <div className={styles.paperRule} />
          <span className={styles.paperLabel}>PAYMENT DETAILS</span>
          {hasBank ? (
            <div className={styles.paperMethod}>
              <strong>Bank transfer</strong>
              <Kv label="Account name" value={bank.name} />
              <Kv label="Account no." value={bank.number} mono />
              <Kv label="Bank, branch" value={bank.branch} />
              <Kv label="Codes" value={bank.codes} />
              <Kv label="SWIFT" value={bank.swift} mono />
            </div>
          ) : null}
          {number ? (
            <div className={styles.paperMethod}>
              <strong>{till ? "M-Pesa Buy Goods" : "M-Pesa"}</strong>
              <Kv label={till ? "Till" : "Pay Bill"} value={number} mono />
              <Kv label="Account" value={account} mono />
            </div>
          ) : null}
          {!hasBank && !number ? (
            <span className={styles.empty}>No bank account or M-Pesa: only the pay link's QR code.</span>
          ) : null}
          {contact ? (
            <>
              <div className={styles.paperRule} />
              <span className={styles.paperMuted}>Questions? {contact}</span>
            </>
          ) : null}
        </div>
      </div>
      <p className={styles.note}>
        Payment details show only while an invoice is owed. Downloading or sending an invoice again uses what is saved
        here.
      </p>
    </aside>
  );
}
